import { Link } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { esperaConferencia, liquidoDe, precisaDeCategoria } from '@domain/finance'
import type { Account, Category, CreditCard, Transaction } from '@domain/finance'
import { formatBRL } from '@shared/money/format'
import { IconeBanco, IconeCartao, IconeCheque } from '@ui/shell/icones'
import { useSegurar } from '@ui/shell/segurar'
import { SeloPendente } from './SeloPendente'
import { SeletorDeCategoria } from './SeletorDeCategoria'
import { totalmenteAbatida } from './abatimento/vinculos'
import type { DicaDoAbatimento } from './abatimento/vinculos'

/**
 * O que a transação significa para as somas do mês. Ver `Transaction.md`.
 */
const IMPACT_LABELS: Record<Transaction['cashImpact'], string> = {
  direct: 'à vista',
  deferred: 'no cartão',
  settlement: 'pagamento de fatura',
  // Fora das duas somas de propósito: é a mesma movimentação vista pelo outro
  // lado. O rótulo diz isso em vez de esconder — quem vir a linha na lista e
  // não a vir no total precisa entender por quê.
  internal: 'espelho interno',
}

/**
 * Uma transação na lista: **uma linha só**, que abre para categorizar.
 *
 * ## Por que uma linha, e o que ela desistiu de mostrar
 *
 * A versão anterior era `flex flex-wrap` com cinco pedaços do mesmo peso —
 * descrição, categoria, impacto, valor, selo — e nada alinhado: numa tela de
 * 390px o valor caía na primeira linha de uma transação e na segunda da
 * seguinte, porque quebrava onde coubesse. Não havia coluna de valor; havia
 * texto que às vezes caía no mesmo lugar.
 *
 * Agora a linha carrega o que se lê de relance — instrumento, nome, valor — e
 * **desiste da categoria**, que passou a viver na abertura. É uma troca, e o que
 * se ganha é densidade: cabem ~16 transações na dobra contra ~11.
 *
 * ## O ponto substitui a palavra "sem categoria"
 *
 * Repetida em 46 linhas, ela era uma parede que dizia sempre a mesma coisa. O
 * ponto marca o mesmo estado ocupando 5px, e o chamado à ação mora no contador
 * do topo, que já existe e já leva ao Classificar.
 *
 * ## A LINHA INTEIRA abre, e por isso a descrição deixou de ser link
 *
 * Antes a descrição era um `<Link>` para o detalhe. Um link de texto dentro de
 * uma linha que também responde ao toque são dois alvos disputando o mesmo dedo,
 * e o menor deles tem 44px só na vertical. Agora o alvo é a linha, e "Abrir a
 * transação" mora dentro da abertura — um toque a mais para quem quer o detalhe,
 * nenhum erro de mira para quem quer categorizar.
 *
 * ## O ícone diz o INSTRUMENTO, que o texto não dizia
 *
 * "no cartão" e "à vista" são `cashImpact`, não instrumento. Medido no livro:
 * em 89 transações — os pares de pagamento de fatura, `creditCard + internal` e
 * `account + settlement` — o rótulo fica mudo sobre cartão ou conta, e são
 * justamente as que aparecem duas vezes, uma de cada lado.
 *
 * ## O painel mostra o que a linha escondeu
 *
 * Instrumento por extenso, impacto no caixa, e a categoria com o **caminho
 * inteiro**: `alimentacao / mercado` diz qual "Mercado" é, o que o nome sozinho
 * não faz numa árvore que repete nome entre ramos.
 *
 * ## O seletor de tela cheia aqui NÃO contradiz a decisão 250b
 *
 * Ela preservou o `<select>` nativo onde *"a escolha é um detalhe de uma linha
 * entre dezenas, e abrir a tela inteira para cada uma tiraria de vista a lista
 * que se está varrendo"*. A escolha deixou de ser isso: ela mora num painel que
 * a pessoa abriu de propósito, um por vez, e quem varre a lista não esbarra
 * nela. O que ficou de pé do argumento é a nota e o classificar, onde a célula
 * continua dentro da linha.
 */
export function LinhaDeTransacao({
  transaction,
  categories,
  creditCards,
  accounts,
  transfers,
  aberta,
  onAbrir,
  onCategorize,
  onCategoriasMudaram,
  onConfirmar,
  dica = null,
}: {
  transaction: Transaction
  categories: Category[]
  creditCards: CreditCard[]
  accounts: Account[]
  transfers: Set<string>
  aberta: boolean
  onAbrir: () => void
  onCategorize: (id: string, categoryId: string | null) => Promise<void>
  onCategoriasMudaram: () => void
  /** Confere a categoria que a tabela de regras aplicou — Fase 10. */
  onConfirmar: (id: string) => Promise<void>
  /**
   * Quem abateu este gasto, ou para qual gasto esta entrada foi usada — a
   * dica do hover e a abertura no celular. Montada por quem tem a lista do
   * mês (`dicaDoAbatimento`), para passar o mouse não custar leitura.
   */
  dica?: DicaDoAbatimento | null
}) {
  const [gravando, setGravando] = useState(false)
  const [falhou, setFalhou] = useState(false)
  const categoryId = transaction.enrichment.categoryId
  // Azul: a tabela decidiu e ninguém conferiu ainda. Amarelo: sem categoria —
  // e só quando falta alguém decidir: a compra que as entradas zeraram não pede
  // nada, e amarelo nela seria trabalho inventado (`precisaDeCategoria`).
  const porConferir = esperaConferencia(transaction.enrichment.categorySource)
  const tom =
    categoryId === null
      ? precisaDeCategoria(transaction)
        ? 'pendente'
        : 'neutro'
      : porConferir
        ? 'automatico'
        : 'neutro'
  // O abatimento — `Docs/05-Plano/Abatimento de gasto.md`. A entrada ligada
  // diz o que abate; o gasto abatido diz quanto fica com a casa.
  const vinculo = transaction.enrichment.offsetOf
  const abatimento = transaction.enrichment.offset
  // A compra que as entradas zeraram sai RISCADA, nome e valor, como a linha
  // tirada da soma na nota: o valor do extrato continua à vista — é o dado
  // original —, mas não conta. Abatida em parte não risca: ela ainda é gasto,
  // e o canto diz quanto fica.
  const riscada = totalmenteAbatida(transaction)
  const idDaDica = useId()
  const cartao = transaction.source.instrument.type === 'creditCard'
  const segurar = useSegurar(onAbrir)

  /** Grava, desliga o controle enquanto isso, e diz quando falha. */
  function gravar(o: () => Promise<void>) {
    setGravando(true)
    setFalhou(false)
    void o()
      .catch(() => {
        setFalhou(true)
      })
      .finally(() => {
        setGravando(false)
      })
  }

  return (
    <li className="border-b border-[var(--border)]">
      <div
        // **`py-1`, e não `py-2`.** A linha ganhou dois andares e mediu 87px no
        // navegador; o pedido do dono foi explícito em manter compacto. Os
        // 44px do alvo do nome e os 44 do seletor continuam inteiros — o que
        // encolhe é o respiro entre linhas, e o `gap-0` junta os andares que
        // são da mesma transação.
        className="sem-preview group/abatimento relative flex touch-pan-y flex-col py-1 pl-3 select-none"
        onPointerDown={segurar.aoApertar}
        onPointerMove={(e) => {
          segurar.aoMover(e)
        }}
        onPointerUp={segurar.encerrar}
        onPointerCancel={segurar.encerrar}
        // Segurar sobre um link seleciona texto no desktop e abre o menu do
        // sistema no celular. Os dois competem com o painel.
        onContextMenu={(e) => {
          e.preventDefault()
        }}
        // Segurar termina num `click` que o navegador dispara de qualquer jeito.
        // Sem a trava na captura, segurar abriria o painel **e** navegaria.
        onClickCapture={segurar.aoClicarNaCaptura}
      >
        <div className="flex items-center gap-2">
        {/* A porta acessível do painel: o ícone já estava aqui, e virar botão
            custa zero pixel e dá o alvo de 44px a quem não segura. */}
        <button
          type="button"
          onClick={onAbrir}
          aria-expanded={aberta}
          // "Detalhes", e não "Categoria": o seletor lá dentro leva esse nome,
          // e dois controles com o mesmo nome acessível na mesma linha é o
          // defeito que a decisão 246e conserta em outro lugar. O painel mostra
          // instrumento, impacto e categoria — "detalhes" é o que ele é.
          aria-label={`Detalhes de ${transaction.source.description}`}
          // Esc fecha o painel aberto antes de fazer qualquer outra coisa.
          data-nav-expansivel
          className="alvo shrink-0 px-0 text-[var(--text-faint)]"
        >
          {cartao ? <IconeCartao /> : <IconeBanco />}
        </button>
        <Link
          to="/transacoes/$id"
          params={{ id: transaction.id }}
          // O alvo das setas: ↑↓ andam por aqui, Enter abre. Ver `teclas.ts`.
          data-nav-linha
          aria-describedby={dica === null ? undefined : idDaDica}
          className="alvo min-w-0 flex-1 justify-start gap-2"
        >
          <span
            className={`min-w-0 flex-1 truncate ${
              riscada ? 'text-[var(--text-muted)] line-through' : ''
            }`}
          >
            {transaction.source.description}
          </span>
          <span
            className={`shrink-0 whitespace-nowrap tabular-nums ${riscada ? 'line-through' : ''}`}
            // Saída em coral, entrada em menta. A cor faz a varredura da coluna
            // antes da leitura — e é a semântica de VALOR da paleta, não o tom
            // da marca, que significa ação.
            // A entrada que abate um gasto perde a cor de entrada: ela não é
            // receita da casa. Não é riscada — o dinheiro entrou.
            style={{
              color:
                vinculo !== null || riscada
                  ? 'var(--text-muted)'
                  : transaction.source.amountCents < 0
                    ? 'var(--negative)'
                    : 'var(--positive)',
            }}
          >
            {formatBRL(transaction.source.amountCents)}
          </span>
        </Link>
        </div>

        {/* A dica do abatimento: quem abateu este gasto, ou para qual gasto
            esta entrada foi usada (pedido do dono). No hover e no foco do
            teclado, ligada ao link por `aria-describedby` — e por isso o
            leitor de tela a anuncia. Sem hover (celular), a mesma informação
            está na abertura, logo abaixo. `hover:` do Tailwind 4 só vale onde
            há ponteiro que paira, então o toque não a deixa presa na tela. */}
        {dica !== null && (
          <div
            id={idDaDica}
            role="tooltip"
            className="pointer-events-none absolute right-2 bottom-full z-20 mb-1 hidden max-w-[min(22rem,85vw)] rounded-lg border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-xs shadow-lg group-focus-within/abatimento:block group-hover/abatimento:block"
          >
            <p className="mb-0.5 text-[var(--text-muted)]">{dica.titulo}</p>
            {dica.linhas.map((linha) => (
              <p key={linha} className="tabular-nums">
                {linha}
              </p>
            ))}
          </div>
        )}

        {/* **Segundo andar: a categoria EMBAIXO DO NOME, e o status à direita.**
            Ela morava só na abertura, e por isso ninguém sabia em que categoria
            uma linha tinha caído sem abrir uma por uma. A cor diz a
            PROCEDÊNCIA: amarelo sem categoria, azul posta pela tabela de regras
            e ainda não conferida, neutro quando alguém decidiu (Fase 10).

            **À esquerda, e não à direita** (pedido do dono, decisão 298): a
            categoria pertence ao nome, e no canto direito ela lia como se
            pertencesse ao valor — que é de outra natureza. O recuo de 36px a
            põe na mesma coluna do nome, que é a calha do ícone.

            O status subiu para esta linha, no canto: ele era um terceiro andar
            para dizer uma palavra, e a linha inteira pagava a altura. */}
        <div className="flex items-center gap-2 pl-9">
          {vinculo !== null ? (
            // Sem seletor: a categoria de uma entrada ligada não conta em soma
            // nenhuma, ela segue o gasto que abate.
            <span className="min-w-0 truncate text-xs text-[var(--text-muted)]">
              {`abate ${vinculo.description}`}
            </span>
          ) : (
            <>
              <SeletorDeCategoria
                compacto
                tom={tom}
                categorias={categories}
                value={categoryId}
                rotulo={`Categoria de ${transaction.source.description}: ${nomeDaCategoria(categoryId, categories)}`}
                disabled={gravando}
                className="max-w-full"
                onCriou={onCategoriasMudaram}
                onChange={(escolhida) => {
                  gravar(() => onCategorize(transaction.id, escolhida))
                }}
              />
              {/* Conferir é um gesto próprio: quem concorda com o que a tabela fez
                  não quer reescolher a mesma categoria para tirar o azul. */}
              {porConferir && (
                <button
                  type="button"
                  disabled={gravando}
                  aria-label={`Confirmar a categoria de ${transaction.source.description}`}
                  title="Confirmar a categoria"
                  className="alvo shrink-0 text-[var(--text-muted)]"
                  onClick={() => {
                    gravar(() => onConfirmar(transaction.id))
                  }}
                >
                  <IconeCheque />
                </button>
              )}
            </>
          )}

          {/* No canto, e só quando há o que dizer: a maioria das linhas não
              tem status nenhum, e o espaço volta para o nome. */}
          <span className="ml-auto flex items-center gap-2">
            {/* O que fica com a casa depois do abatimento — o valor da linha
                continua sendo o do extrato, que é o dado original. */}
            {abatimento !== null && (
              <span className="text-xs whitespace-nowrap text-[var(--text-muted)] tabular-nums">
                {liquidoDe(transaction) === 0
                  ? 'abatida'
                  : `fica ${formatBRL(-liquidoDe(transaction))}`}
              </span>
            )}
            <SeloPendente status={transaction.status} />
          </span>
        </div>

        {/* O erro mora aqui, e não na abertura: quem categoriza pela linha não
            abre nada, e um aviso escondido atrás de um toque é um aviso que
            ninguém lê. */}
        {falhou && (
          <p role="alert" className="pl-9 text-xs text-[var(--text-muted)]">
            não foi possível salvar
          </p>
        )}
      </div>

      {aberta && (
        <div className="pb-2 pl-3 text-xs">
          <dl className="mb-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[var(--text-muted)]">
            {/* O NOME do cartão ou da conta, e não "cartão" de novo: o ícone da
                linha já disse o tipo, e a linha dizia duas vezes a mesma coisa
                — visto em produção, "Cartão · no cartão". O impacto no caixa
                vem ao lado porque é outra pergunta: de que soma isto participa. */}
            <dt>{cartao ? 'Cartão' : 'Conta'}</dt>
            <dd className="text-[var(--text)]">
              {instrumentoDe(transaction, creditCards, accounts)}
              <span className="text-[var(--text-muted)]">
                {` · ${IMPACT_LABELS[transaction.cashImpact]}`}
              </span>
            </dd>
            {/* Só quando HÁ categoria: sem ela, o seletor logo abaixo já diz
                "Sem categoria", e a linha repetiria a mesma frase a 4px de
                distância. Com ela, esta linha não é repetição — ela mostra o
                CAMINHO, que o gatilho do seletor não mostra. */}
            {dica !== null && (
              <>
                <dt>{dica.titulo}</dt>
                <dd className="text-[var(--text)]">
                  {dica.linhas.map((linha) => (
                    <span key={linha} className="block tabular-nums">
                      {linha}
                    </span>
                  ))}
                </dd>
              </>
            )}
            {categoryId !== null && (
              <>
                <dt>Categoria</dt>
                <dd className="text-[var(--text)]">{caminhoDe(categoryId, categories)}</dd>
              </>
            )}
          </dl>

          {categoryId !== null && transfers.has(categoryId) && (
            <p className="mb-2 text-[var(--text-muted)]">transferência — fora dos totais</p>
          )}
          {transaction.status === 'canceled' && (
            <p className="mb-2 text-[var(--text-muted)]">cancelada — fora dos totais</p>
          )}
        </div>
      )}
    </li>
  )
}

/** O nome do cartão ou da conta de onde a transação saiu. */
function instrumentoDe(
  transaction: Transaction,
  creditCards: CreditCard[],
  accounts: Account[],
): string {
  const { instrument } = transaction.source
  return instrument.type === 'creditCard'
    ? (creditCards.find((c) => c.id === instrument.id)?.name ?? 'cartão que não existe mais')
    : (accounts.find((a) => a.id === instrument.id)?.name ?? 'conta que não existe mais')
}

/**
 * O caminho inteiro da categoria — `alimentacao / mercado`.
 *
 * O nome sozinho não basta: a árvore repete nome entre ramos ("Geral" de Casa e
 * "Geral" de Transporte), e é o caminho que diz qual é qual. Mesma escolha do
 * painel de tela cheia quando se busca.
 */
function caminhoDe(categoryId: string | null, categories: Category[]): string | null {
  if (categoryId === null) return null
  const c = categories.find((x) => x.id === categoryId)
  if (c === undefined) return 'categoria que não existe mais'
  return c.path.join(' / ')
}

/** O nome da categoria, para o rótulo do seletor dizer o estado. */
function nomeDaCategoria(categoryId: string | null, categories: Category[]): string {
  if (categoryId === null) return 'sem categoria'
  return categories.find((c) => c.id === categoryId)?.name ?? 'categoria apagada'
}
